import * as React from 'react';
import { withApollo } from 'react-apollo';

import type ApolloClient from 'apollo-client';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import connectToStores from 'js/lib/connectToStores';
import user from 'js/lib/user';

import CookiesContext from 'bundles/page/contexts/CookiesContext';
import CsrfTokenContext from 'bundles/page/contexts/CsrfTokenContext';
import PageLoadEventContext from 'bundles/page/contexts/PageLoadEventContext';
import RequestCountryCodeContext from 'bundles/page/contexts/RequestCountryCodeContext';
import RouterContext from 'bundles/page/contexts/RouterContext';
import UserAgentApiContext from 'bundles/page/contexts/UserAgentApiContext';
import type { Cookies, UserAgentApi } from 'bundles/page/types';
import type { ServerRouterData } from 'bundles/page/types/router';
import withHasPageLoaded from 'bundles/page/utils/withHasPageLoaded';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import 'css!bundles/front-page/styl/index';

type Props = {
  userAgentApi: UserAgentApi;
  cookies: Cookies;
  router: ServerRouterData;
  children: React.ReactNode;
  requestCountryCode: string;
  csrfToken?: string;
  hasPageLoaded?: boolean;
  client: ApolloClient<$TSFixMe>;
  isPreview: boolean;
};

export class DataFetchBoundary extends React.LegacyComponentWithChildren {
  // short circuit tree walking to make ssr faster/more efficient
  dataFetchingBoundary = true;

  render() {
    const { children } = this.props;
    return children;
  }
}

// To be used at page level, injects common contexts.
export class FrontPageApplicationWithPrefetch extends React.LegacyComponentWithChildren<Props> {
  render() {
    return (
      <DataFetchBoundary>
        <RouterContext.Provider value={this.props.router}>
          <UserAgentApiContext.Provider value={this.props.userAgentApi}>
            <CookiesContext.Provider value={this.props.cookies}>
              <CsrfTokenContext.Provider value={this.props.csrfToken || '<MISSING>'}>
                <RequestCountryCodeContext.Provider value={this.props.requestCountryCode || 'US'}>
                  <PageLoadEventContext.Provider value={{ hasPageLoaded: this.props.hasPageLoaded || false }}>
                    {this.props.children}
                  </PageLoadEventContext.Provider>
                </RequestCountryCodeContext.Provider>
              </CsrfTokenContext.Provider>
            </CookiesContext.Provider>
          </UserAgentApiContext.Provider>
        </RouterContext.Provider>
      </DataFetchBoundary>
    );
  }
}

export default compose<Props, {}>(
  connectToRouter((router) => {
    const location = router.location;
    const previewParam = location?.query?.preview;
    const isPreview = !!user.isSuperuser() && !!previewParam && Number(previewParam) === 1;
    return {
      router,
      isPreview,
    };
  }),
  connectToStores<Props, {}>([ApplicationStoreClass], (ApplicationStore, props) => {
    const appState = ApplicationStore.getState();
    return {
      ...props,
      csrfToken: appState.csrfToken,
      requestCountryCode: appState.requestCountryCode,
      cookies: appState.cookies,
      userAgentApi: appState.userAgent,
    };
  }),
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'front_page',
        page: 'front_page_story',
      },
    };
  }),
  withHasPageLoaded,
  withApollo
)(FrontPageApplicationWithPrefetch);
